import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, FileText, Link as LinkIcon } from "lucide-react";
import { errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { HandbookViewerModal } from "../components/HandbookViewerModal.js";
import { AsyncButton } from "../components/ui/async-button.js";

const API_BASE = "";

interface HandbookInfo {
  alreadySigned: boolean;
  firstName?: string;
  signedName?: string;
  signedAt?: string;
  handbook?: { title: string; version: string; readUrl: string };
}

export default function HandbookSignPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState<HandbookInfo | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ signedName: string; signedAt: string } | null>(null);

  useEffect(() => {
    if (!token) { setError("No token provided."); setLoading(false); return; }
    axios.get<HandbookInfo>(`${API_BASE}/api/handbook/sign/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((err) => { logError("HandbookSignPage.load", err); setError(errMsg(err) ?? "This link is invalid."); })
      .finally(() => setLoading(false));
  }, [token]);

  async function submit() {
    setSubmitting(true); setError("");
    try {
      const { data } = await axios.post<{ signedName: string; signedAt: string }>(
        `${API_BASE}/api/handbook/sign/${token}`,
        { signedName: signedName.trim() },
      );
      setDone(data);
    } catch (err) {
      logError("HandbookSignPage.submit", err);
      setError(errMsg(err) ?? "Failed to submit. Please try again.");
      throw err;
    } finally { setSubmitting(false); }
  }

  if (loading) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontSize: 15, color: "var(--color-text-secondary)" }}>Loading…</div>;
  }

  if (error) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <div style={{ maxWidth: 400, textAlign: "center", padding: 32 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}><LinkIcon size={48} /></div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: "var(--color-text)" }}>Link Unavailable</h2>
          <p style={{ color: "var(--color-text-secondary)", fontSize: 14 }}>{error}</p>
          <p style={{ color: "var(--color-text-400)", fontSize: 13, marginTop: 12 }}>Please contact HR to get a new link.</p>
        </div>
      </div>
    );
  }

  if (done || info?.alreadySigned) {
    const name = done?.signedName ?? info?.signedName ?? "";
    const at = done?.signedAt ?? info?.signedAt ?? "";
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <div style={{ maxWidth: 420, textAlign: "center", padding: 32 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, color: "var(--color-success-accent)" }}><CheckCircle2 size={56} /></div>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>{done ? "Thank you!" : "Already signed"}</h2>
          <p style={{ color: "var(--color-text-secondary)", fontSize: 14 }}>
            Signed by {name} on {at ? new Date(at).toLocaleDateString() : ""}.
          </p>
        </div>
      </div>
    );
  }

  const handbook = info?.handbook;

  return (
    <div style={{ background: "var(--color-surface-50)", minHeight: "100vh", paddingBottom: 100 }}>
      <div style={{ background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-border)", padding: "16px 24px" }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "var(--color-text)" }}>Sign the Employee Handbook</h1>
        {info?.firstName && <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--color-text-secondary)" }}>Hi {info.firstName}, HR has requested your signature below.</p>}
      </div>

      <div style={{ maxWidth: 640, margin: "24px auto", padding: "0 16px" }}>
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-border)", borderRadius: 12, padding: 24 }}>
          {!handbook ? (
            <p style={{ color: "var(--color-text-secondary)", fontSize: 14 }}>No handbook is currently available to sign. Please contact HR.</p>
          ) : (
            <>
              <h2 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700 }}>{handbook.title} (v{handbook.version})</h2>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--color-text-secondary)" }}>
                Please review the full document below, then type your name to sign.
              </p>
              <button
                onClick={() => setShowModal(true)}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1.5px solid var(--color-info-accent)", background: "var(--color-surface-0)", color: "var(--color-info-fg-strong)", cursor: "pointer", fontSize: 13, fontWeight: 600, marginBottom: 20 }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><FileText size={14} /> View Handbook</span>
              </button>

              {!reachedEnd && (
                <p style={{ fontSize: 12, color: "var(--color-text-400)" }}>View the handbook and scroll to the end to enable signing.</p>
              )}

              {reachedEnd && (
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4, color: "var(--color-text-700)" }}>Type your full name to sign</label>
                  <input
                    type="text"
                    value={signedName}
                    onChange={(e) => setSignedName(e.target.value)}
                    placeholder="Full name"
                    style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid var(--color-surface-300)", fontSize: 15, width: "100%", boxSizing: "border-box", marginBottom: 8 }}
                  />
                  <p style={{ margin: "0 0 16px", fontSize: 11, color: "var(--color-text-400)" }}>
                    By typing your name and clicking below, you confirm you have read and agree to the {handbook.title}.
                  </p>
                  <AsyncButton
                    onClick={() => submit()}
                    disabled={!signedName.trim() || submitting}
                    variant="info"
                    className="w-full"
                  >
                    I have read and agree — Sign
                  </AsyncButton>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showModal && handbook && (
        <HandbookViewerModal
          url={handbook.readUrl}
          title={handbook.title}
          version={handbook.version}
          onReachedEnd={() => setReachedEnd(true)}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
